import React, { useEffect, useState } from 'react';
import { useGoogleLogin, useGoogleOneTapLogin } from '@react-oauth/google';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithCredential, GoogleAuthProvider, type OAuthCredential } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { beginGmailOAuth } from '../utils/gmailOAuth';
import { ensurePlan } from '../utils/plan';

const API_BASE = process.env.REACT_APP_API_URL ?? "http://localhost:5050";

type UserRecord = {
  gmail_connected?: boolean;
  hasCompletedOnboarding?: boolean;
};

const SIGN_IN_FAILED = 'We couldn’t sign you in. Please try again. If it keeps happening, email help@oncaramel.com.';
const GMAIL_CONNECT_FAILED =
  'We signed you in, but couldn’t connect your Gmail. Please try again. If it keeps happening, email help@oncaramel.com.';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wrongGmail = searchParams.get('gmail_error') === 'wrong_account';
  const [submitting, setSubmitting] = useState(false);
  const [signInError, setSignInError] = useState('');
  // Hovering or focusing "Continue with Google" warms the panel: a deeper
  // colour, a marker under "starts here.", a darker subline
  const [warm, setWarm] = useState(false);

  // Wake the backend while they pick an account: it stops when idle and takes
  // ~15s to start, and connecting Gmail right after sign-in waits on it.
  useEffect(() => {
    fetch(`${API_BASE}/`).catch(() => {});
  }, []);

  // Signs in to Firebase, creates the user doc on first sign-in, then routes on.
  const completeSignIn = async (credential: OAuthCredential) => {
    let connectingGmail = false;
    try {
      setSubmitting(true);
      setSignInError('');
      const userCredential = await signInWithCredential(auth, credential);

      const db = getFirestore();
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data() as UserRecord | undefined;
      const needsGmailConnection = !userDoc.exists() || !userData?.gmail_connected;

      // New users: their profile here; the plan is the backend's to set (ensurePlan)
      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          email: userCredential.user.email,
          name: userCredential.user.displayName,
          photo_url: userCredential.user.photoURL,
          gmail_connected: false,
          gmail_connected_at: null,
          created_at: Timestamp.now(),
        });
      }
      // Not awaited: it calls the backend, which can take ~20s to wake on Fly.
      // Nothing here needs the plan, and Home asks again if it's missing (useEnsurePlan).
      ensurePlan();

      if (needsGmailConnection) {
        connectingGmail = true;
        await beginGmailOAuth({
          user: userCredential.user,
          apiBase: API_BASE,
          // Reconnecting (Google expired the Gmail token) goes back to Home, not
          // through onboarding again: onboarding is for new users only.
          successRedirect: `${window.location.origin}${userData?.hasCompletedOnboarding ? '/dashboard/home' : '/onboarding'}`,
          errorRedirect: `${window.location.origin}/`,
        });
        return;
      }

      if (userData?.hasCompletedOnboarding) {
        navigate('/dashboard/home');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      console.error('Firebase sign-in failed:', error);
      setSignInError(connectingGmail ? GMAIL_CONNECT_FAILED : SIGN_IN_FAILED);
    } finally {
      setSubmitting(false);
    }
  };

  // Custom button: Google popup returns an access token, which Firebase accepts.
  const signInWithGoogle = useGoogleLogin({
    onSuccess: (token) => completeSignIn(GoogleAuthProvider.credential(null, token.access_token)),
    onError: () => {
      console.error('Google Login Failed');
      setSignInError(SIGN_IN_FAILED);
    },
  });

  // One Tap prompt returns an ID token.
  useGoogleOneTapLogin({
    onSuccess: (response) => {
      if (response.credential) completeSignIn(GoogleAuthProvider.credential(response.credential));
    },
    onError: () => console.error('Google One Tap Failed'),
  });

  const link = 'text-[#7A5238] underline underline-offset-[3px] hover:text-melt-text1';
  const ease = 'ease-[cubic-bezier(.23,1,.32,1)] motion-reduce:transition-none';

  return (
    <main className="flex min-h-screen bg-[#FBF9F5] font-grotesk text-[#5C3417] antialiased max-[860px]:flex-col">
      <section
        className={`m-4 flex w-[44%] min-w-[420px] flex-col rounded-[22px] px-12 py-11 transition-colors duration-[320ms] max-[860px]:min-h-[460px] max-[860px]:w-auto max-[860px]:min-w-0 ${ease} ${
          warm ? 'bg-[#EFD1A6]' : 'bg-[#F3E2C9]'
        }`}
      >
        <div className="flex items-center gap-2.5 text-[18px] font-semibold">
          <img src="/logo-medium.svg" alt="" className="h-7 w-7" />
          Caramel
        </div>
        <div className="mt-auto">
          <h2 className="font-quicksand text-[clamp(44px,5vw,66px)] font-bold leading-[1.04] tracking-[-.035em]">
            Your job hunt{' '}
            <span
              // The landing page's headline marker (.mark), darker to show on the warm panel
              className={`rounded-[.12em] transition-[background-size] duration-[550ms] [box-decoration-break:clone] ${ease}`}
              style={{
                background: 'linear-gradient(rgba(217,136,41,.4), rgba(217,136,41,.4)) 0 88% / 0% 46% no-repeat',
                backgroundSize: warm ? '100% 46%' : '0% 46%',
              }}
            >
              starts{'\u00a0'}here.
            </span>
          </h2>
          <p
            className={`mt-[18px] text-[18px] transition-colors duration-[320ms] ${ease} ${
              warm ? 'text-[#6B4529]' : 'text-[#7A5238]'
            }`}
          >
            Let's make it a little sweeter.
          </p>
        </div>
      </section>

      <section className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="flex w-full max-w-[380px] flex-col gap-3.5">
          <div className="mb-1">{/* 4px + the 14px gap: 18px under "Welcome to Caramel" */}
            <h1 className="font-quicksand text-[40px] font-bold leading-none tracking-[-.035em]">Sign in</h1>
            <p className="mt-2 text-[16px] text-[#7A5238]">Welcome to Caramel</p>
          </div>

          <button
            type="button"
            onClick={() => signInWithGoogle()}
            disabled={submitting}
            onPointerEnter={() => setWarm(true)}
            onPointerLeave={() => setWarm(false)}
            onFocus={() => setWarm(true)}
            onBlur={() => setWarm(false)}
            className={`flex h-[52px] w-full items-center justify-center gap-3 rounded-[12px] border border-[rgba(92,52,23,.28)] bg-white text-[15.5px] font-semibold text-[#5C3417] transition-[background-color,transform] duration-[160ms] hover:bg-[#FBF6EE] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[#D98829] active:scale-[.97] disabled:cursor-default disabled:opacity-60 ${ease}`}
          >
            <span className="grid h-6 w-6 place-items-center rounded-full bg-white">
              <img src="/google-g.png" alt="" className="h-5 w-5" />
            </span>
            {submitting ? 'Signing in…' : 'Continue with Google'}
          </button>

          {/* Back from Gmail connect: they approved a different Google account (backend/gmail_connect.py) */}
          {(signInError || wrongGmail) && (
            <p role="alert" className="rounded-[14px] bg-[#EFD1A6] px-3.5 py-3 text-[13.5px] leading-normal text-[#5C3417]">
              {signInError ||
                'That Gmail didn’t match the account you signed in with, so it wasn’t connected. Sign in again and choose the same account on both screens.'}
            </p>
          )}

          {/* Reminder: Gmail sync reads the inbox they sign in with */}
          <p className="rounded-[14px] border border-[rgba(92,52,23,.12)] px-3.5 py-3 text-[13.5px] leading-normal text-[#7A5238]">
            Sign in with{' '}
            <strong className="font-semibold text-[#5C3417]">the email you use to apply for jobs.</strong>
          </p>

          <p className="text-center font-plex text-[12px] leading-[1.6] text-[#8A6A52]">
            By continuing, you agree to our <a href="https://oncaramel.com/terms" target="_blank" rel="noopener noreferrer" className={link}>Terms of Service</a> and{' '}
            <a href="https://oncaramel.com/privacy" target="_blank" rel="noopener noreferrer" className={link}>Privacy Policy</a>.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Login;
